import { fetchWithToken } from '@utils/api';
import { API_URL } from '@data/constants';
import { EventArea } from '@services/eventsAreas';
import { EventType } from '@services/eventsTypes';

export interface Event {
  id: number;
  fullName: string;
  location: string;
  edition: string;
  startEvent: string;
  endEvent: string;
  startRegistration: string;
  endRegistration: string;
  userAlreadyRegistered: boolean;
  eventType: EventType;
  eventAreas: EventArea[];
  openForRegistration: boolean;
}

export interface EventForm {
  location: string;
  edition: string;
  startEvent: string;
  endEvent: string;
  startRegistration: string;
  endRegistration: string;
  eventTypeId: string;
  eventAreaIds: string[];
}

export async function getEvents({
  open_for_registration = undefined,
  ended = undefined,
  ordering = undefined,
}: {
  open_for_registration?: boolean;
  ended?: boolean;
  ordering?: string;
} = {}): Promise<{ results: Event[]; count: number }> {
  const queryParams = new URLSearchParams();

  if (open_for_registration !== undefined) {
    queryParams.append(
      'open_for_registration',
      open_for_registration.toString()
    );
  }

  if (ended !== undefined) {
    queryParams.append('ended', ended.toString());
  }

  if (ordering) {
    queryParams.append('ordering', ordering);
  }

  const response = await fetchWithToken(
    `${API_URL}/api/events?${queryParams.toString()}`
  );

  if (!response.ok) {
    throw new Error('Error fetching events');
  }

  const { data } = (await response.json()) as {
    data: { results: Event[]; count: number };
  };

  return data;
}

export async function createEvent(event: EventForm): Promise<Event> {
  const response = await fetchWithToken(`${API_URL}/api/events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(event),
  });

  if (!response.ok) {
    throw new Error('Error creating event');
  }

  const { data } = (await response.json()) as { data: Event };

  return data;
}

export async function updateEvent(
  eventId: number,
  event: EventForm
): Promise<Event> {
  const response = await fetchWithToken(`${API_URL}/api/events/${eventId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(event),
  });

  if (!response.ok) {
    throw new Error('Error updating event');
  }

  const { data } = (await response.json()) as { data: Event };

  return data;
}

export async function deleteEvent(eventId: number): Promise<void> {
  const response = await fetchWithToken(`${API_URL}/api/events/${eventId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Error deleting event');
  }
}
